import React, { useState } from 'react';
import axios from 'axios';
import { Workflow } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface WorkflowOrganizationProps {
  workflow: Workflow;
  onUpdate: (updatedWorkflow: Workflow) => void;
}

const WorkflowOrganization: React.FC<WorkflowOrganizationProps> = ({ workflow, onUpdate }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [category, setCategory] = useState(workflow.category || 'General');
  const [tags, setTags] = useState<string[]>(workflow.tags || []);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);

  const categories = [
    'General',
    'Image Generation',
    'Text Processing',
    'Video Generation',
    'Audio Processing'
  ];

  const commonTags = [
    'stable-diffusion',
    'comfyui',
    'workflow',
    'ai-art',
    'upscaling',
    'enhancement',
    'portrait',
    'landscape',
    'animation',
    'text-to-image'
  ];

  const addTag = (tag: string) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
    setNewTag('');
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/workflows/${workflow.id}/organization`, {
        category,
        tags
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const updatedWorkflow = {
        ...workflow,
        category,
        tags
      };
      
      onUpdate(updatedWorkflow);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update workflow organization:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setCategory(workflow.category || 'General');
    setTags(workflow.tags || []);
    setNewTag('');
    setIsEditing(false);
  };

  // Only show to admins
  if (!user || (user.role !== 'super_admin' && user.role !== 'admin')) {
    return null;
  }

  if (!isEditing) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mt-4">
        <div className="flex justify-between items-start mb-3">
          <h4 className="text-sm font-medium text-gray-700">Organization</h4>
          <button
            onClick={() => setIsEditing(true)}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Edit
          </button>
        </div>
        
        <div className="space-y-2">
          <div>
            <span className="text-xs text-gray-500">Category:</span>
            <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
              {workflow.category || 'General'}
            </span>
          </div>
          
          {workflow.tags && workflow.tags.length > 0 && (
            <div>
              <span className="text-xs text-gray-500">Tags:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {workflow.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mt-4">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-sm font-medium text-gray-700">Edit Organization</h4>
        <div className="flex gap-2">
          <button
            onClick={cancelEdit}
            className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded"
          >
            Cancel
          </button>
          <button
            onClick={saveChanges}
            disabled={saving}
            className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Category Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Tags Management */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Tags</label>
          
          {/* Current Tags */}
          <div className="flex flex-wrap gap-1 mb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs flex items-center gap-1"
              >
                #{tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="text-blue-600 hover:text-blue-800 ml-1"
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          {/* Add New Tag */}
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Add tag..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTag(newTag)}
              className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={() => addTag(newTag)}
              className="bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs"
            >
              Add
            </button>
          </div>

          {/* Common Tags */}
          <div>
            <span className="text-xs text-gray-500 mb-1 block">Quick add:</span>
            <div className="flex flex-wrap gap-1">
              {commonTags.filter(tag => !tags.includes(tag)).map((tag) => (
                <button
                  key={tag}
                  onClick={() => addTag(tag)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                >
                  +{tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowOrganization;
